'use strict'

import $ from 'jquery'
import _ from 'lodash'
import * as EmailValidator from 'email-validator'
import { selectors } from '../selectors'
import { selectorsCustomer } from '../selectors_customer'
import { ATTR } from './helpers'
import { elementExists } from '../shared'
import regex from './regex.js'

/**
 * resolve(null) -> skip
 * resolve(true) -> success
 * resolve(message) -> success with custom feedback
 * reject() -> failure
 * reject(message) -> failure with custom feedback
 */

// ---------------------- Confirm Password Match ----------------------

export const confirmPasswordMatchCustom = ({ selector }, { resolve, reject }) => {
    const targetSelector = $(selector).attr('data-confirm-password')
    const confirmValue = $(selector).val()
    const passwordValue = $(targetSelector).val()

    confirmValue === passwordValue ? resolve(true) : reject('Passwords do not match.')
}

// ---------------------- Registration personal info ----------------------

export const testAgainstMultiple = ({ selector }, { resolve, reject }) => {
    let value = $(selector).val().trim()

    // Reduce multiple spaces into one
    value = value.replace(/\s{2,}/g, ' ')

    $(selector).val(value)
    if (!value.match(regex.startWithLetter)) return reject('Name must start and end with a letter.')
    if (!value.match(regex.endWithLetter)) return reject('Name must start and end with a letter.')
    if (!value.match(regex.mustNotExceed)) return reject('Name must not exceed 50 characters.')

    if (value.match(regex.noConsecutiveCharacters)) return reject('Name cannot contain consecutive special characters.')
    if (value.match(regex.noConsecutiveSpaces)) return reject('Name cannot contain consecutive special characters.')
    if (value.match(regex.invalidCharacters) || value.match(regex.noSpaceSurroundedSpecial)) {
        return reject('Name can only contain letters, hyphens, apostrophes, spaces, and periods.')
    }

    resolve(true)
}

// ---------------------- Email ----------------------

export const emailCustom = ({ selector }, { resolve, reject }) => {
    const $input = $(selector)

    EmailValidator.validate($input.val()) ? resolve(true) : reject()
}

// ---------------------- Address ----------------------

export const addressCustomInputValidation = ({ selector }, { resolve, reject }) => {
    const selectors = {
        houseNumber: '#registration_form_step_address_streetNumber',
        houseName: '#registration_form_step_address_houseName',
        client_houseNumber: '#client_information_address_update_streetNumber',
        client_houseName: '#client_information_address_update_houseName',
    }

    const possibleInput = {
        [selectors.houseNumber]: {
            relation: selectors.houseName,
            regex: regex.streetNumber,
        },
        [selectors.houseName]: {
            relation: selectors.houseNumber,
            regex: regex.houseName,
        },
        [selectors.client_houseNumber]: {
            relation: selectors.client_houseName,
            regex: regex.streetNumber,
        },
        [selectors.client_houseName]: {
            relation: selectors.client_houseNumber,
            regex: regex.houseName,
        },
    }

    const currentInputEmpty = !!$(selector)._isEmpty()
    const relationInputEmpty = !!$(possibleInput[selector].relation)._isEmpty()

    if (!!$(selector).val().match(possibleInput[selector].regex)) {
        if (currentInputEmpty && relationInputEmpty) reject()

        if (currentInputEmpty && !relationInputEmpty) {
            resolve(null)
        }

        if (!currentInputEmpty && relationInputEmpty) {
            forceInitState(possibleInput[selector].relation)
            resolve(true)
        }

        resolve(true)
    }

    reject()
}

const removeInvalid = (selector) => {
    const $element = $(selector)
    $element.attr(ATTR.VALID._CLIENT, ATTR.STATE._INIT)
    $element.attr(ATTR.VALID._CUSTOM, ATTR.STATE._INIT)
}

const forceInitState = (selector) => {
    const $element = $(selector)
    $element.attr(ATTR.VALID._CLIENT, ATTR.STATE._SUCCESS)
    $element.attr(ATTR.VALID._CUSTOM, ATTR.STATE._SUCCESS)
    $element.removeClass('is-invalid')
    $element.parents('div.form-group').find('div.invalid-feedback').hide()
    $element.parents('div.form-group').find('div.valid-feedback').show()
}

// ---------------------- Contract ----------------------

export const contractCustom = ({ selectors, requirePhone = true, readStatus = {} }, { resolve, reject }) => {
    const checkboxesValid = selectors.every((checkbox) => $(checkbox).is(':checked'))
    const phoneValid = requirePhone ? isPhoneChecked : true
    const documentKeys = Object.keys(readStatus)
    const allDocumentsRead = documentKeys.length > 0 && documentKeys.every((key) => readStatus[key])

    if (checkboxesValid && isEmailChecked && phoneValid && allDocumentsRead) {
        resolve()
    } else {
        reject()
    }
    const result = {
        verification_email: isEmailChecked,
        verification_privacy: checkboxesValid,
        ...Object.fromEntries(documentKeys.map((key) => [key, !!readStatus[key]])),
    }

    // Per-checkbox validation
    selectors.forEach((checkbox) => {
        const id = $(checkbox).attr('id')
        if (id) {
            result['verification_checkbox_' + id] = $(checkbox).is(':checked')
        }
    })

    if (requirePhone) {
        result.verification_phone = isPhoneChecked
    }
    return result
}

// ---------------------- Repay Other ----------------------

export const repayOtherCustom = ({ selector }, { resolve, reject }) => {
    const MESSAGES = {
        empty: 'Invalid value entered',
        nan: 'Only numbers allowed',
        limit: 'Limit exceeded',
    }

    const $input = $(selector)
    const maxValue = parseFloat($input.attr('max'))
    const inputValue = parseFloat($input.val())

    if (!inputValue) {
        return reject(MESSAGES.empty)
    }

    const isNumber = !isNaN(inputValue) && !isNaN(maxValue)
    if (!isNumber) {
        return reject(MESSAGES.nan)
    }

    const inLimit = isNumber && inputValue <= maxValue
    if (!inLimit) {
        return reject(MESSAGES.limit)
    }

    return resolve(true)
}

// ---------------------- Employment ----------------------

export const employmentCustom = ({ selector }, { resolve, reject }) => {
    const INPUTS_REGISTRATION = [selectors.inputs.monthlyIncome, selectors.inputs.totalMonthlyIncome]
    const INPUTS_CLIENT = [selectorsCustomer.inputs.customerEmploymentMonthlyIncome, selectorsCustomer.inputs.customerEmploymentTotalMonthlyIncome]
    const INPUTS_CLIENT_RPP = [selectorsCustomer.inputs.reducedPlanIncome, selectorsCustomer.inputs.reducedPlanIncomeOther]

    const incomeSelectors = inputs([INPUTS_REGISTRATION, INPUTS_CLIENT, INPUTS_CLIENT_RPP])

    rejectIfValueZero(selector, reject)
    rejectIfIncomeInvalid(incomeSelectors, selector, reject)
    resolve(true)
}

const rejectIfIncomeInvalid = ([netMonthlyIncome, totalNetMonthlyIncome], currentInput, reject) => {
    // if both values aren't in skip validation
    if ($(netMonthlyIncome)._isEmpty() || $(totalNetMonthlyIncome)._isEmpty()) return

    const errorMap = {
        [netMonthlyIncome]: 'Must be less then Total Net Monthly Income.',
        [totalNetMonthlyIncome]: 'Must be more then Net Monthly Income.',
    }

    parseInt($(netMonthlyIncome).val()) > parseInt($(totalNetMonthlyIncome).val()) && reject(errorMap[currentInput])

    // if both values are valid force success state to other input to
    const [otherInput] = [netMonthlyIncome, totalNetMonthlyIncome].filter((input) => input !== currentInput)

    $(otherInput).val() !== '0' && $(otherInput)._forceSuccess(ATTR.VALID._CUSTOM, true)
}

const rejectIfValueZero = (selector, reject) => {
    const MESSAGES = { incomeZero: 'Check again! Income cannot be 0.' }
    $(selector).val() === '0' && reject(MESSAGES.incomeZero)
}

export const earlySettlementOtherCustom = ({ selector }, { resolve, reject }) => {
    const reason = $(selectorsCustomer.selects.earlySettlementReason).val()
    const $input = $(selector)
    const value = $input.val().trim()

    const minLength = parseInt($input.attr('data-min-length') ?? '0')
    const maxLength = parseInt($input.attr('data-max-length') ?? '255')

    if (reason !== 'other') {
        $input.val('')
        return resolve(true)
    }

    if (value.length === 0) {
        return reject()
    }

    if (value.length < minLength) {
        return reject($input.attr('data-error-client-min-length') ?? $input.attr(ATTR.ERROR._CLIENT))
    }

    if (value.length > maxLength) {
        return reject($input.attr('data-error-client-max-length') ?? $input.attr(ATTR.ERROR._CLIENT))
    }

    return resolve(true)
}

const updateEarlySettlementVisibility = () => {
    const $reason = $(selectorsCustomer.selects.earlySettlementReason)
    const $input = $(selectorsCustomer.inputs.earlySettlementOther)
    const $wrapper = $input.closest('#other-reason')

    let show = false

    if ($reason.val() === 'other') {
        show = true
    }

    $wrapper.toggle(show)
}

export const earlySettlementReasonCustom = ({ selector }, { resolve, reject }) => {
    updateEarlySettlementVisibility()

    return resolve(true)
}

// ---------------------- Client RPP ----------------------

export const checkInputLimit = ({ selectors }, { resolve, reject }) => {
    const errorMessage = 'You’ve entered more than the amount due.'

    const $amountOwedDisplay = $(selectorsCustomer.inputs.reducedPlanOwed)
    const $amountPaymentInput = $(selectorsCustomer.inputs.reducedPlanPayment)

    const amountOwedToNumber = Number($amountOwedDisplay.val().replace('£', ''))
    const amountPaymentToNumber = Number($amountPaymentInput.val())

    amountOwedToNumber < amountPaymentToNumber ? reject(errorMessage) : resolve(true)
}

// ---------------------- Helpers ----------------------

const inputs = (groups) => {
    return groups.find((group) => group.every(elementExists)) ?? []
}
